import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import React, { type ReactNode, forwardRef, useState } from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import { Icon } from '../../assets/Icon/Icon';
import { outlineColors, outlines } from '../../common/outlines';
import { useControlSize } from '../../controls/shared/context';
import { selectors, transitions } from '../../controls/shared/styles';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { sizer } from '../../utilities/shared/sizes';

type ToggleInputSize = 'small' | 'large';

export type ToggleInputProps = {
  'aria-describedby'?: string;
  /**
   * Provide an accessible label when no visible label accompanies the toggle.
   */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /**
   * Adds indeterminate as a third stop the user can click back to (indeterminate → on → off →
   * indeterminate), e.g. to undo a change in a bulk edit. `onValueChange` reports every change,
   * `onChange` only the on and off ones, and `selected` and `indeterminate` must be controlled.
   */
  canResetToIndeterminate?: boolean;
  /**
   * Displayed as a trailing label for the toggle.
   */
  children?: ReactNode;
  /**
   * Set the initial state for an uncontrolled toggle.
   */
  defaultSelected?: boolean;
  /**
   * Set if the component is disabled.
   */
  disabled?: boolean;
  id?: string;
  /**
   * Set if the component is in an indeterminate state. Overrides `selected` if true.
   */
  indeterminate?: boolean;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  onChange?: (selected: boolean) => void;
  /**
   * Called on every change when `canResetToIndeterminate` is set. `undefined` means indeterminate.
   */
  onValueChange?: (value: boolean | undefined) => void;
  /**
   * Set if the component is selected.
   */
  selected?: boolean;
  /**
   * Set the size utilizing the [`controlSize` API](/?path=/docs/handlers-providers--use-control-size).
   */
  size?: ToggleInputSize;
  value?: string;
  variant?: 'standard' | 'polarity' | 'positive';
  negative?: {
    icon?: IconName;
    label?: React.ReactNode;
  };
  neutral?: {
    icon?: IconName;
    label?: React.ReactNode;
  };
  positive?: {
    icon?: IconName;
    label?: React.ReactNode;
  };
};

const ToggleInputTabIcon = styled(Icon, {
  display: 'flex',
  variants: {
    active: {
      true: {},
      false: {},
    },
    variant: {
      standard: {},
      polarity: {},
      positive: {},
    },
    size: {
      small: {
        width: '$8',
        height: '$8',
      },
      large: {
        width: '$10',
        height: '$10',
      },
    },
  },
  compoundVariants: [
    {
      active: true,
      variant: 'polarity',
      css: {
        color: colors.iconPositiveLight,
      },
    },
    {
      active: true,
      variant: 'positive',
      css: {
        color: colors.iconPositiveLight,
      },
    },
    {
      active: false,
      variant: 'polarity',
      css: {
        color: colors.iconNegativeLight,
      },
    },
    {
      active: false,
      variant: 'positive',
      css: {
        color: colors.iconAlternativeLight,
      },
    },
  ],
});
ToggleInputTabIcon.displayName = 'ToggleInputTabIcon';

const transformActiveSmall = sizer(6);
const transformActiveLarge = sizer(10);
const transformIndeterminateSmall = sizer(3);
const transformIndeterminateLarge = sizer(5);
const transformTriStateActiveSmall = sizer(18);
const transformTriStateActiveLarge = sizer(22);
const transformTriStateIndeterminateSmall = sizer(9);
const transformTriStateIndeterminateLarge = sizer(11);

const ToggleInputTab = styled(BaseCheckbox.Indicator, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '99em',
  backgroundColor: colors.white,
  outline: outlines.control,
  outlineColor: outlineColors.control.raisedInitialLight,
  boxShadow: shadows.controlRaisedInitialLight,
  pointerEvents: 'none',
  transition: transitions.control,

  [selectors.hover]: {
    outlineColor: outlineColors.control.raisedHoveredLight,
    boxShadow: shadows.controlRaisedHoveredLight,
  },

  [darkThemeSelector]: {
    outlineColor: outlineColors.control.raisedInitialDark,
    boxShadow: shadows.controlRaisedInitialDark,

    [selectors.hover]: {
      outlineColor: outlineColors.control.raisedHoveredDark,
      boxShadow: shadows.controlRaisedHoveredDark,
    },
  },
  variants: {
    active: {
      true: {},
      false: {},
    },
    indeterminate: {
      true: {
        alignItems: 'center',
        justifyContent: 'center',

        '&:before': {
          content: '',
          position: 'absolute',
          display: 'block',
          overflow: 'hidden',
          background: colors.controlStrokeBaseLight,
          borderRadius: '99em',
        },
      },
    },
    size: {
      small: {
        width: '$10',
        height: '$10',

        '&:before': {
          width: '$4',
          height: '$1',
        },
      },
      large: {
        width: '$14',
        height: '$14',

        '&::before': {
          width: '$6',
          height: '$1',
        },
      },
    },
    triState: {
      true: {},
    },
  },
  compoundVariants: [
    {
      active: true,
      size: 'small',
      css: {
        transform: `translateX(${transformActiveSmall})`,
      },
    },
    {
      active: true,
      size: 'large',
      css: {
        transform: `translateX(${transformActiveLarge})`,
      },
    },
    {
      indeterminate: true,
      size: 'small',
      css: {
        transform: `translateX(${transformIndeterminateSmall})`,
      },
    },
    {
      indeterminate: true,
      size: 'large',
      css: {
        transform: `translateX(${transformIndeterminateLarge})`,
      },
    },
    {
      triState: true,
      active: true,
      size: 'small',
      css: {
        transform: `translateX(${transformTriStateActiveSmall})`,
      },
    },
    {
      triState: true,
      active: true,
      size: 'large',
      css: {
        transform: `translateX(${transformTriStateActiveLarge})`,
      },
    },
    {
      triState: true,
      indeterminate: true,
      size: 'small',
      css: {
        transform: `translateX(${transformTriStateIndeterminateSmall})`,
      },
    },
    {
      triState: true,
      indeterminate: true,
      size: 'large',
      css: {
        transform: `translateX(${transformTriStateIndeterminateLarge})`,
      },
    },
  ],
});
ToggleInputTab.displayName = 'ToggleInputTab';

const ToggleInputNotch = styled('span', {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '$4',
  height: '$4',
  borderRadius: '99em',
  backgroundColor: colors.gray300,
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
  transition: transitions.control,

  variants: {
    active: {
      true: {
        backgroundColor: colors.white,
        opacity: 0.6,
      },
      false: {},
    },
    variant: {
      standard: {},
      polarity: {},
      positive: {},
    },
  },
  compoundVariants: [
    {
      active: false,
      variant: 'polarity',
      css: {
        backgroundColor: colors.white,
        opacity: 0.6,
      },
    },
    {
      active: false,
      variant: 'positive',
      css: {
        backgroundColor: colors.white,
        opacity: 0.6,
      },
    },
  ],
});
ToggleInputNotch.displayName = 'ToggleInputNotch';

const ToggleInputTrack = styled('span', {
  display: 'inline-flex',
  flex: 'none',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: colors.gray100,
  borderRadius: '99em',
  transition: transitions.control,

  [selectors.focus]: {
    boxShadow: shadows.focusRingLight,
  },

  [darkThemeSelector]: {
    backgroundColor: colors.gray600,

    [selectors.focus]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  '&[data-disabled]': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  variants: {
    active: {
      true: {
        backgroundColor: colors.brand600,

        [darkThemeSelector]: {
          backgroundColor: colors.brand600,
        },
      },
    },
    indeterminate: {
      true: {
        backgroundColor: colors.gray100,

        [darkThemeSelector]: {
          backgroundColor: colors.gray600,
        },
      },
    },
    size: {
      small: {
        width: '$24',
        height: '$16',
        padding: '0 $4',
      },
      large: {
        width: '$32',
        height: '$20',
        padding: '0 $4',
      },
    },
    triState: {
      true: {
        position: 'relative',
      },
    },
    variant: {
      standard: {},
      polarity: {},
      positive: {},
    },
  },
  compoundVariants: [
    {
      triState: true,
      size: 'small',
      css: {
        width: '$36',
      },
    },
    {
      triState: true,
      size: 'large',
      css: {
        width: '$44',
      },
    },
    {
      active: true,
      variant: 'positive',
      css: {
        backgroundColor: colors.green600,

        [darkThemeSelector]: {
          backgroundColor: colors.green600,
        },
      },
    },
    {
      active: true,
      variant: 'polarity',
      css: {
        backgroundColor: colors.green600,

        [darkThemeSelector]: {
          backgroundColor: colors.green600,
        },
      },
    },
    {
      active: false,
      variant: 'polarity',
      css: {
        backgroundColor: colors.red600,

        [darkThemeSelector]: {
          backgroundColor: colors.red600,
        },
      },
    },
    {
      active: false,
      variant: 'positive',
      css: {
        backgroundColor: colors.blue600,

        [darkThemeSelector]: {
          backgroundColor: colors.blue600,
        },
      },
    },
    {
      indeterminate: true,
      variant: 'polarity',
      css: {
        backgroundColor: colors.gray100,

        [darkThemeSelector]: {
          backgroundColor: colors.gray600,
        },
      },
    },
    {
      indeterminate: true,
      variant: 'positive',
      css: {
        backgroundColor: colors.gray100,

        [darkThemeSelector]: {
          backgroundColor: colors.gray600,
        },
      },
    },
  ],
});
ToggleInputTrack.displayName = 'ToggleInputTrack';

const ToggleInputEnds = styled(Text, {
  userSelect: 'none',
  whiteSpace: 'nowrap',
  color: colors.headingNeutralLight,

  [darkThemeSelector]: {
    color: colors.headingNeutralDark,
  },

  variants: {
    size: {
      small: {
        fontSize: '$12',
        lineHeight: '$16',
      },
      large: {
        fontSize: '$14',
        lineHeight: '$20',
      },
    },
  },
});
ToggleInputEnds.displayName = 'ToggleInputEnds';

export const ToggleInputContainer = styled('label', {
  display: 'flex',
  alignItems: 'center',
  gap: '$6',
  cursor: 'pointer',
  variants: {
    disabled: {
      true: {
        cursor: 'not-allowed',
      },
      false: {},
    },
  },
});
ToggleInputContainer.displayName = 'ToggleInputContainer';

function nextTriStateValue(indeterminate: boolean | undefined, checked: boolean) {
  if (indeterminate) {
    return true;
  }
  if (checked) {
    return false;
  }
  return undefined;
}

export const ToggleInput = forwardRef<HTMLLabelElement, ToggleInputProps>(
  (
    {
      'aria-describedby': ariaDescribedBy,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      canResetToIndeterminate = false,
      children,
      defaultSelected,
      disabled = false,
      id,
      indeterminate,
      name,
      negative,
      neutral,
      onBlur,
      onChange,
      onValueChange,
      positive,
      selected,
      size = 'large',
      value,
      variant,
    },
    forwardedRef,
  ) => {
    const controlSize = useControlSize(size, 'large');
    const effectiveSize =
      controlSize === 'x-large' || controlSize === 'large' || controlSize === 'medium'
        ? 'large'
        : 'small';

    const [uncontrolledSelected, setUncontrolledSelected] = useState(defaultSelected ?? false);
    const checked = selected ?? uncontrolledSelected;
    const isSelected = checked && !indeterminate;

    // An empty wrapping <label> would win Base UI's label discovery and blank out the
    // accessible name from an external <Label htmlFor> or ancestor label, so only render one
    // when it will contain visible text.
    const hasInlineLabel =
      Boolean(children) ||
      ((variant === 'positive' || variant === 'polarity') &&
        Boolean(neutral?.label || negative?.label || positive?.label));

    return (
      <ToggleInputContainer
        as={hasInlineLabel ? 'label' : 'span'}
        disabled={disabled}
        ref={forwardedRef}
      >
        {(variant === 'positive' || variant === 'polarity') &&
          (neutral?.label || negative?.label) && (
            <ToggleInputEnds size={effectiveSize}>
              {neutral?.label || negative?.label}
            </ToggleInputEnds>
          )}
        <BaseCheckbox.Root
          render={
            <ToggleInputTrack
              active={isSelected}
              size={effectiveSize}
              indeterminate={indeterminate}
              triState={canResetToIndeterminate}
              variant={variant}
            />
          }
          aria-describedby={ariaDescribedBy}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          checked={checked}
          disabled={disabled}
          id={id}
          indeterminate={indeterminate}
          name={name}
          onBlur={onBlur}
          onCheckedChange={() => {
            if (canResetToIndeterminate) {
              const nextValue = nextTriStateValue(indeterminate, checked);
              if (nextValue !== undefined) {
                onChange?.(nextValue);
              }
              onValueChange?.(nextValue);
              return;
            }
            const nextSelected = indeterminate || !checked;
            if (selected === undefined) {
              setUncontrolledSelected(nextSelected);
            }
            onChange?.(nextSelected);
          }}
          value={value}
        >
          {canResetToIndeterminate && <ToggleInputNotch active={isSelected} variant={variant} />}
          <ToggleInputTab
            keepMounted
            active={isSelected}
            size={effectiveSize}
            indeterminate={indeterminate}
            triState={canResetToIndeterminate}
          >
            {!indeterminate && (variant === 'polarity' || variant === 'positive') && (
              <ToggleInputTabIcon
                variant={variant}
                active={isSelected}
                icon={
                  isSelected
                    ? positive?.icon || 'checkmark'
                    : neutral?.icon || negative?.icon || 'cross'
                }
                size={effectiveSize}
              />
            )}
          </ToggleInputTab>
        </BaseCheckbox.Root>
        {(variant === 'positive' || variant === 'polarity') && positive?.label && (
          <ToggleInputEnds size={effectiveSize}>{positive.label}</ToggleInputEnds>
        )}
        {children && <ToggleInputEnds size={effectiveSize}>{children}</ToggleInputEnds>}
      </ToggleInputContainer>
    );
  },
);
